import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { apiErr, fileUrl } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import ExportPreview from "@/components/ExportPreview";
import { dueText, kmText } from "@/components/VehicleDueAlert";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, FileText, Wrench, Fuel, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const km = (n) => Number(n || 0).toLocaleString("id-ID");

export default function VehicleHistory() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [tipe, setTipe] = useState("all");
  const [pdf, setPdf] = useState(false);
  const [foto, setFoto] = useState(null);
  const qs = new URLSearchParams({ vehicle_id: id, ...(start ? { start } : {}), ...(end ? { end } : {}) }).toString();
  useEffect(() => { api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch((e) => toast.error(apiErr(e))); }, [id]);
  useEffect(() => { api.get(`/vehicle-report?${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))); }, [qs]);
  const rows = useMemo(() => {
    if (!data) return [];
    const s = data.services.map((r) => ({ ...r, tipe: "service" }));
    const f = data.fuel.map((r) => ({ ...r, tipe: "bbm" }));
    return [...s, ...f].filter((r) => tipe === "all" || r.tipe === tipe)
      .sort((a, b) => b.tanggal.localeCompare(a.tanggal) || Number(b.km || 0) - Number(a.km || 0));
  }, [data, tipe]);
  if (!v) return <div className="p-8 text-slate-400">Memuat...</div>;
  const liter = data ? data.fuel.reduce((s, r) => s + Number(r.liter || 0), 0) : 0;
  return (
    <div className="space-y-5" data-testid="vehicle-history">
      <PageHeader title={`Riwayat ${v.plat_no}`} subtitle={`${v.jenis}${v.merk ? ` · ${v.merk}` : ""}${v.tahun ? ` · ${v.tahun}` : ""}`}>
        <Link to="/vehicles"><Button variant="outline" size="sm" data-testid="vh-back"><ArrowLeft className="h-4 w-4 mr-1" /> Kembali</Button></Link>
        <Button size="sm" className="bg-blue-800 hover:bg-blue-900" onClick={() => setPdf(true)} data-testid="vh-pdf-btn"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
      </PageHeader>
      <VehicleInfo v={v} />
      {data && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={`Service (${data.services.length}x)`} value={rupiah(data.total_service)} testid="vh-total-service" tone="bg-blue-900 text-white" />
        <Stat label={`BBM (${data.fuel.length}x)`} value={rupiah(data.total_bbm)} testid="vh-total-bbm" tone="bg-orange-500 text-white" />
        <Stat label="Total Liter BBM" value={`${liter.toLocaleString("id-ID")} L`} testid="vh-total-liter" tone="bg-white" />
        <Stat label="Total Biaya Kendaraan" value={rupiah(data.total_service + data.total_bbm)} testid="vh-total" tone="bg-slate-900 text-white" />
      </div>}
      <Card className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <label className="text-xs font-semibold text-slate-600">Dari<Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="vh-start" /></label>
        <label className="text-xs font-semibold text-slate-600">Sampai<Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="vh-end" /></label>
        <div className="sm:col-span-2 flex gap-2">
          {[["all", "Semua"], ["service", "Service"], ["bbm", "BBM"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={tipe === k ? "default" : "outline"} className={tipe === k ? "bg-orange-600 hover:bg-orange-700" : ""} onClick={() => setTipe(k)} data-testid={`vh-tipe-${k}`}>{l}</Button>
          ))}
        </div>
      </Card>
      <Timeline rows={rows} onFoto={setFoto} />
      <ExportPreview open={pdf} onClose={() => setPdf(false)} title={`Riwayat Kendaraan ${v.plat_no}`} pdfUrl={pdf ? `/vehicle-report/pdf?${qs}` : null}
        items={[{ label: "Unduh PDF", url: `/vehicle-report/pdf?${qs}`, filename: `riwayat_${v.plat_no.replace(/\s+/g, "_")}.pdf` }]} />
      <Dialog open={!!foto} onOpenChange={(o) => { if (!o) setFoto(null); }}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Foto Nota</DialogTitle></DialogHeader>{foto && <img src={foto} alt="nota" className="w-full rounded" />}</DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, testid, tone }) => (
  <Card className={`p-4 ${tone}`}><div className="text-xs font-semibold opacity-80">{label}</div><div className="mt-1 text-lg sm:text-xl font-black font-mono truncate" data-testid={testid}>{value}</div></Card>
);

const VehicleInfo = ({ v }) => {
  const tone = (late, warn) => (late ? "text-rose-600" : warn ? "text-amber-600" : "text-slate-800");
  const has = (x) => x !== null && x !== undefined;
  const items = [
    ["Pajak", v.tgl_pajak ? `${formatDate(v.tgl_pajak)} (${dueText(v.sisa_pajak)})` : "-", tone(has(v.sisa_pajak) && v.sisa_pajak < 0, has(v.sisa_pajak) && v.sisa_pajak <= 30)],
    ["STNK", v.tgl_stnk ? `${formatDate(v.tgl_stnk)} (${dueText(v.sisa_stnk)})` : "-", tone(has(v.sisa_stnk) && v.sisa_stnk < 0, has(v.sisa_stnk) && v.sisa_stnk <= 30)],
    ["KM Terakhir", `${km(v.km_terakhir)} km`, "text-slate-800"],
    ["Ganti Oli Berikut", v.km_service_berikut ? `${km(v.km_service_berikut)} km (${kmText(v.sisa_km)})` : "Belum ada data", tone(has(v.sisa_km) && v.sisa_km < 0, has(v.sisa_km) && v.sisa_km <= 500)],
  ];
  return (
    <Card className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="vh-info">
      {items.map(([l, val, c]) => <div key={l}><div className="text-xs text-slate-500">{l}</div><div className={`text-sm font-bold ${c}`}>{val}</div></div>)}
    </Card>
  );
};

const Timeline = ({ rows, onFoto }) => (
  <Card className="p-0 overflow-x-auto scroll-thin">
    <table className="w-full text-sm" data-testid="vh-table">
      <thead><tr className="bg-blue-900 text-white text-xs uppercase">
        {["Tanggal", "Jenis", "KM", "Rincian", "Diinput", "Nota", "Nominal"].map((h) => <th key={h} className={`px-3 py-2.5 ${h === "Nominal" ? "text-right" : "text-left"}`}>{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">Belum ada riwayat.</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`vh-row-${i}`}>
            <td className="px-3 py-2 font-mono whitespace-nowrap">{formatDate(r.tanggal)}</td>
            <td className="px-3 py-2">{r.tipe === "service"
              ? <span className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-800"><Wrench className="h-3 w-3" /> Service</span>
              : <span className="inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-700"><Fuel className="h-3 w-3" /> BBM</span>}</td>
            <td className="px-3 py-2 font-mono">{r.km ? km(r.km) : "-"}</td>
            <td className="px-3 py-2 text-xs">{r.tipe === "service"
              ? <>{r.bengkel && <div className="font-semibold">{r.bengkel}</div>}{(r.items || []).map((it, j) => <div key={j}>{it.nama} × {it.qty} — {rupiah(it.jumlah)}</div>)}</>
              : <>{r.jenis_bbm || "BBM"}{r.liter ? ` · ${r.liter} L` : ""}{r.spbu ? ` · ${r.spbu}` : ""}</>}
              {r.keterangan && <div className="text-slate-400">{r.keterangan}</div>}</td>
            <td className="px-3 py-2 text-xs">{r.mandor_nama || r.created_by}</td>
            <td className="px-3 py-2">{r.foto_path ? <button className="text-blue-700" onClick={() => onFoto(fileUrl(r.foto_path))} data-testid={`vh-foto-${i}`}><ImageIcon className="h-4 w-4" /></button> : <span className="text-slate-300">—</span>}</td>
            <td className="px-3 py-2 text-right font-mono font-semibold whitespace-nowrap">{rupiah(r.total)}</td>
          </tr>
        ))}
      </tbody>
      {rows.length > 0 && <tfoot><tr className="bg-orange-50 font-bold" data-testid="vh-table-total">
        <td colSpan={6} className="px-3 py-2">TOTAL ({rows.length} catatan)</td>
        <td className="px-3 py-2 text-right font-mono">{rupiah(rows.reduce((s, r) => s + Number(r.total || 0), 0))}</td>
      </tr></tfoot>}
    </table>
  </Card>
);
